/**
 * PoolDetails Component - Phase 3H-U Redesign
 * 
 * Comprehensive pool detail view with membership info.
 * BSL Compliant: Factual display only.
 * 
 * REDESIGNED: New stats layout per beta requirements
 * - Top section: Pool overview with actionable stats
 * - Your Stats: Performance metrics with efficiency/effectiveness
 * - Footer: Pool end date
 */

import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Pool, PoolMembership } from '../../types';
import { 
  Card, CardHeader, CardTitle, CardContent,
  Button, StatCard, StatCardGrid, Badge
} from '../common';
import { ModeBadge, StatusBadge } from '../common';
import { formatDate, getDomainLabel } from '../../utils';

interface EntryState {
  filledSlots: number;
  totalSlots: number;
  unitsInPlay: number;
  isLocked: boolean;
}

interface PoolStats {
  totalParticipants: number;
  lockedParticipants: number;
  userRank: number | null;
}

interface PoolDetailsProps {
  pool: Pool;
  membership?: PoolMembership | null;
  onJoin?: () => void;
  className?: string;
}

export function PoolDetails({
  pool,
  membership,
  onJoin,
  className = '',
}: PoolDetailsProps): JSX.Element {
  const navigate = useNavigate();
  const isMember = !!membership;
  const canJoin = !isMember && pool.status === 'active';

  const [entryState, setEntryState] = useState<EntryState>({
    filledSlots: 0,
    totalSlots: 7,
    unitsInPlay: 0,
    isLocked: false
  });
  
  const [poolStats, setPoolStats] = useState<PoolStats>({
    totalParticipants: pool.participant_count || 0,
    lockedParticipants: 0,
    userRank: null
  });

  const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

  // Fetch entry and pool stats
  useEffect(() => {
    const fetchData = async () => {
      const userId = localStorage.getItem('current_user_id');
      if (!userId || !pool.pool_id) return;

      // Fetch user's entry
      try {
        const entryRes = await fetch(`${API_BASE}/pools/${pool.pool_id}/entry`, {
          headers: { 'X-User-ID': userId }
        });
        if (entryRes.ok) {
          const entryData = await entryRes.json();
          if (entryData.entry) {
            const filled = entryData.entry.roster?.filter((s: any) => s.player !== null).length || 0;
            setEntryState({
              filledSlots: filled,
              totalSlots: entryData.entry.roster?.length || 7,
              unitsInPlay: entryData.entry.totalUnits || 0,
              isLocked: entryData.state === 'LOCKED' || entryData.state === 'COMPLETED'
            });
          }
        }
      } catch (err) {
        console.error('Failed to fetch entry:', err);
      }

      // Fetch leaderboard for pool stats and user rank
      try {
        const lbRes = await fetch(`${API_BASE}/pools/${pool.pool_id}/leaderboard`);
        if (lbRes.ok) {
          const lbData = await lbRes.json();
          const entries = lbData.entries || [];
          // Entries in leaderboard with total_units > 0 are locked
          const lockedCount = entries.filter((e: any) => e.is_locked).length;
          const userEntry = entries.find((e: any) => e.user_id === userId);
          
          setPoolStats({
            totalParticipants: lbData.total_participants || pool.participant_count || 0,
            lockedParticipants: lockedCount,
            userRank: userEntry?.rank || null
          });
        }
      } catch (err) {
        console.error('Failed to fetch leaderboard:', err);
      }
    };

    fetchData();
  }, [pool.pool_id, API_BASE]);

  // Extract week number from pool name
  const extractWeek = (poolName: string): string => {
    const match = poolName.match(/Week\s*(\d+)/i);
    return match ? match[1] : '-';
  };

  // Calculate derived stats
  const availableBalance = membership?.current_balance || 0;
  const initialBalance = (membership as any)?.starting_balance || pool.initial_bankroll || 1000;
  const returnRate = initialBalance > 0 
    ? ((availableBalance - initialBalance) / initialBalance) * 100 
    : 0;
  const capitalDeployed = availableBalance > 0 
    ? (entryState.unitsInPlay / availableBalance) * 100 
    : 0;

  // Game Plan Efficiency: Wins / Total Props
  const wins = membership?.correct_predictions ?? membership?.predictions_won ?? 0;
  const totalPredictions = membership?.predictions_made ?? 0;
  const efficiency = totalPredictions > 0 ? (wins / totalPredictions) * 100 : 0;

  // Game Plan Effectiveness: Dollars Won / Total Dollars at Risk
  const totalWon = membership?.total_won ?? 0;
  const totalLost = membership?.total_lost ?? 0;
  const totalRisked = totalWon + totalLost;
  const effectiveness = totalRisked > 0 
    ? (totalWon / totalRisked) * 100 
    : 0;

  const handleBuildRoster = () => {
    navigate(`/pools/${pool.pool_id}/gm`);
  };

  const handleViewGamePlan = () => {
    // Navigate to game plan/strategy view
    navigate(`/pools/${pool.pool_id}/gm?view=strategyHub`);
  };

  // Get roster status display text
  const getRosterStatusText = (): string => {
    const status = `${entryState.filledSlots}/${entryState.totalSlots}`;
    return entryState.isLocked ? `${status} (Locked)` : `${status} (Open)`;
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Pool Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{pool.pool_name}</CardTitle>
              <div className="flex items-center gap-2 mt-2">
                <StatusBadge status={pool.status} />
                <ModeBadge mode={pool.mode} />
                <Badge variant="default">{getDomainLabel(pool.domain)}</Badge>
              </div>
            </div>
            {canJoin && onJoin && (
              <Button variant="primary" onClick={onJoin}>
                Join Pool
              </Button>
            )}
          </div>
        </CardHeader>
        
        <CardContent>
          {/* Pool Overview Stats */}
          <StatCardGrid columns={4}>
            <StatCard
              label="Current Week"
              value={extractWeek(pool.pool_name)}
            />
            <StatCard
              label="Roster Status"
              value={getRosterStatusText()}
            />
            <StatCard
              label="Pool Status"
              value={`${poolStats.lockedParticipants} of ${poolStats.totalParticipants} Locked`}
            />
            <div /> {/* Empty cell for alignment */}
          </StatCardGrid>

          <StatCardGrid columns={4} className="mt-4">
            <StatCard
              label="Current Rank"
              value={poolStats.userRank ? `#${poolStats.userRank}` : '-'}
            />
            <StatCard
              label="Available Balance"
              value={availableBalance}
              format="currency"
            />
            <StatCard
              label="Dollars in Play"
              value={entryState.unitsInPlay}
            />
            <StatCard
              label="% Capital Deployed"
              value={capitalDeployed}
              format="percentage"
            />
          </StatCardGrid>

          {/* Action Buttons */}
          {isMember && (
            <div className="flex gap-3 mt-6">
              <Button variant="primary" onClick={handleBuildRoster}>
                Build Roster
              </Button>
              <Button variant="secondary" onClick={handleViewGamePlan}>
                View Game Plan
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Stats */}
      {membership && (
        <Card>
          <CardHeader>
            <CardTitle>Your Stats</CardTitle>
          </CardHeader>
          <CardContent>
            <StatCardGrid columns={4}>
              <StatCard
                label="Initial Balance"
                value={initialBalance}
                format="currency"
              />
              <StatCard
                label="Current Balance"
                value={availableBalance}
                format="currency"
              />
              <StatCard
                label="Return Rate"
                value={returnRate}
                format="percentage"
              />
              <StatCard
                label="Pool Rank"
                value={poolStats.userRank ? `#${poolStats.userRank} of ${poolStats.totalParticipants}` : '-'}
              />
            </StatCardGrid>

            <StatCardGrid columns={4} className="mt-4">
              <StatCard
                label="Game Plan Efficiency"
                value={efficiency}
                format="percentage"
              />
              <StatCard
                label="Game Plan Effectiveness"
                value={effectiveness}
                format="percentage"
              />
              <StatCard
                label="Record"
                value={`${wins}W - ${membership.incorrect_predictions ?? 0}L`}
              />
              <div /> {/* Empty cell for alignment */}
            </StatCardGrid>
            
            {membership.is_depleted && (
              <div className="mt-4 p-3 bg-slate-800 rounded-lg text-center">
                <span className="text-slate-400">Bankroll Depleted</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Footer - Pool End Date */}
      <div className="text-center text-sm text-slate-500">
        Pool ends: {formatDate(pool.end_date)}
      </div>
    </div>
  );
}
